import { ApplicationFieldSourceBadge } from '@/components/Customers/ApplicationFieldSourceBadge'
import { CustomerApplicationSourceRecordPicker } from '@/components/Customers/CustomerApplicationSourceRecordPicker'
import { CustomerDetailDialog } from '@/components/Customers/CustomerDetailDialog'
import { DuplicateResolutionPanel } from '@/components/Customers/DuplicateResolutionPanel'
import {
  ErpFieldMappingTable,
  buildErpRowsFromFieldValues,
} from '@/components/Customers/ErpFieldMappingTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import type {
  CustomerApplicationFieldValueContract,
  CustomerApplicationSourceType,
  CustomerFieldMappingTarget,
  QuestionnaireSourceRecordArchiveStatus,
  QuestionnaireSourceRecordContract,
} from '@/contracts/customer-application-binding'
import { type DuplicateCustomerSignal, createDuplicateSignals } from '@/contracts/customers'
import { useAuth } from '@/lib/auth'
import type { CustomerApplicationViewMode } from '@/lib/b2b-customer-workflow-ui'
import {
  B2B_PAGE_SIZE_OPTIONS,
  CUSTOMER_APPLICATIONS_DEFAULT_STATUS_FILTER,
  CUSTOMER_APPLICATIONS_PAGE_SIZE,
  getB2bPageCount,
  paginateB2bRows,
} from '@/lib/b2b-list-defaults'
import {
  type BusinessRole,
  type CustomerApplication,
  applicationStatusMeta,
  applicationToCustomer,
  canAccessCustomerApplications,
  customerFilingStatusMeta,
  getApplicationCustomerMatch,
  getApplicationCustomerStatusLabel,
  getBusinessRole,
  getCustomerForApplication,
  getSalesUserByEmail,
  isSalesContributorRole,
  summarizeApplicationFormType,
} from '@/lib/business-workflow'
import {
  type SourceArchiveOverride,
  type SourceArchiveStatusFilter,
  getApplicationSourceArchiveStatus,
  getApplicationSourceRecordId,
  matchesApplicationArchiveStatus,
  toSourceArchiveOverride,
} from '@/lib/customer-application-archive'
import {
  CustomerApplicationBindingError,
  overrideApplicationFieldValue,
} from '@/lib/customer-application-binding'
import {
  archiveQuestionnaireSourceRecord,
  previewCustomerApplicationFromSourceRecord,
} from '@/lib/data-client/customers'
import { useBusinessWorkflowDataClientHydration } from '@/lib/data-client/use-business-workflow-hydration'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { cn } from '@/lib/utils'
import { useBusinessWorkflowStore } from '@/stores/businessWorkflowStore'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  ClipboardList,
  FileText,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Send,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

type ApplicationDraftFieldKey =
  | 'companyName'
  | 'taxId'
  | 'industry'
  | 'companySize'
  | 'website'
  | 'companyAddress'
  | 'companyPhone'
  | 'contactName'
  | 'contactTitle'
  | 'contactEmail'
  | 'contactPhone'
  | 'decisionRole'
  | 'paymentTerms'
  | 'creditLimit'
  | 'salesRegion'
  | 'expectedValue'
  | 'needSummary'
  | 'internalNotes'

type ApplicationDraft = {
  sourceType: CustomerApplicationSourceType
  sourceRecord?: QuestionnaireSourceRecordContract
  fieldValues: CustomerApplicationFieldValueContract[]
  sourceError?: string
  formId: string
  companyName: string
  taxId: string
  industry: string
  companySize: string
  website: string
  companyAddress: string
  companyPhone: string
  contactName: string
  contactTitle: string
  contactEmail: string
  contactPhone: string
  decisionRole: string
  paymentTerms: string
  creditLimit: number
  salesRegion: string
  expectedValue: number
  source: string
  needSummary: string
  internalNotes: string
}

type StatusFilter = CustomerApplication['status'] | 'all'
type ViewMode = CustomerApplicationViewMode

const archiveStatusOptions: Array<{ value: SourceArchiveStatusFilter; label: string }> = [
  { value: 'active', label: '未封存' },
  { value: 'archived', label: '已封存' },
  { value: 'all', label: '全部封存狀態' },
]

const applicationFieldConfigs: Array<{
  key: ApplicationDraftFieldKey
  label: string
  kind: 'text' | 'number' | 'textarea'
  className?: string
}> = [
  { key: 'companyName', label: '公司名稱', kind: 'text' },
  { key: 'taxId', label: '統編 / Tax ID', kind: 'text' },
  { key: 'industry', label: '產業', kind: 'text' },
  { key: 'companySize', label: '公司規模', kind: 'text' },
  { key: 'website', label: '網站', kind: 'text' },
  { key: 'companyAddress', label: '公司地址', kind: 'text' },
  { key: 'companyPhone', label: '公司電話', kind: 'text' },
  { key: 'contactName', label: '聯絡人', kind: 'text' },
  { key: 'contactTitle', label: '職稱', kind: 'text' },
  { key: 'contactEmail', label: 'Email', kind: 'text' },
  { key: 'contactPhone', label: '電話', kind: 'text' },
  { key: 'decisionRole', label: '決策角色', kind: 'text' },
  { key: 'paymentTerms', label: '付款條件', kind: 'text' },
  { key: 'creditLimit', label: '授信額度', kind: 'number' },
  { key: 'salesRegion', label: '業務區域', kind: 'text' },
  { key: 'expectedValue', label: '預估成交金額', kind: 'number' },
  {
    key: 'needSummary',
    label: '需求摘要',
    kind: 'textarea',
    className: 'md:col-span-2 xl:col-span-3',
  },
  {
    key: 'internalNotes',
    label: '內部備註',
    kind: 'textarea',
    className: 'md:col-span-2 xl:col-span-3',
  },
]

const companyFieldKeys: ApplicationDraftFieldKey[] = [
  'companyName',
  'taxId',
  'industry',
  'companySize',
  'website',
  'companyAddress',
  'companyPhone',
]

const companyQuestionFieldKeys: ApplicationDraftFieldKey[] = [
  ...companyFieldKeys,
  'contactName',
  'contactTitle',
  'contactEmail',
  'contactPhone',
  'decisionRole',
]

const salesCreditFieldKeys: ApplicationDraftFieldKey[] = [
  'paymentTerms',
  'creditLimit',
  'salesRegion',
  'expectedValue',
  'needSummary',
]

const managementFieldKeys: ApplicationDraftFieldKey[] = ['internalNotes']

export default function CustomerApplicationsRoute() {
  const { user, isLoading } = useAuth()
  const queryClient = useQueryClient()
  const workflowQuery = useBusinessWorkflowDataClientHydration(!isLoading)
  const hydrateWorkflow = useBusinessWorkflowStore((state) => state.hydrateWorkflow)
  const salesUsers = useBusinessWorkflowStore((state) => state.salesUsers)
  const applications = useBusinessWorkflowStore((state) => state.applications)
  const customers = useBusinessWorkflowStore((state) => state.customers)
  const sourceLinks = useBusinessWorkflowStore((state) => state.sourceLinks)
  const fieldValuesByApplication = useBusinessWorkflowStore((state) => state.fieldValues)
  const featureVisibility = useSystemFeatureVisibility()
  const createApplicationFromSourceRecord = useBusinessWorkflowStore(
    (state) => state.createApplicationFromSourceRecord,
  )
  const createManualApplication = useBusinessWorkflowStore((state) => state.createManualApplication)

  const role = getBusinessRole(user, salesUsers)
  const canAccess = canAccessCustomerApplications(user, salesUsers)
  const effectiveMode = getEffectiveMode(role)
  const persona = getPersonaForMode(effectiveMode, user?.email, salesUsers)
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    CUSTOMER_APPLICATIONS_DEFAULT_STATUS_FILTER,
  )
  const [ownerFilter, setOwnerFilter] = useState('all')
  const [supervisorFilter, setSupervisorFilter] = useState('all')
  const [archiveStatusFilter, setArchiveStatusFilter] =
    useState<SourceArchiveStatusFilter>('active')
  const [sourceArchiveOverrides, setSourceArchiveOverrides] = useState<
    Record<string, SourceArchiveOverride>
  >({})
  const [archivingSourceRecordId, setArchivingSourceRecordId] = useState<string | null>(null)
  const [archiveError, setArchiveError] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(CUSTOMER_APPLICATIONS_PAGE_SIZE)
  const [draft, setDraft] = useState<ApplicationDraft | null>(null)
  const [isPreviewingSource, setIsPreviewingSource] = useState(false)
  const [isSubmittingDraft, setIsSubmittingDraft] = useState(false)
  const [isRefreshingApplications, setIsRefreshingApplications] = useState(false)
  const [selected, setSelected] = useState<CustomerApplication | null>(null)

  const formOptions = useMemo(
    () =>
      Array.from(
        new Map(
          applications.map((application) => [
            application.formId,
            {
              id: application.formId,
              title: application.formTitle,
              description: application.formDescription,
            },
          ]),
        ).values(),
      ),
    [applications],
  )

  const ownerOptions = useMemo(
    () =>
      Array.from(
        new Map(
          applications.map((application) => [
            application.submittedByEmail,
            application.submittedByName,
          ]),
        ).entries(),
      ),
    [applications],
  )

  const supervisorOptions = useMemo(
    () =>
      Array.from(
        new Map(
          applications.map((application) => [
            application.supervisorEmail,
            application.supervisorName,
          ]),
        ).entries(),
      ),
    [applications],
  )

  const visibleApplications = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase()
    return applications.filter((application) => {
      const scoped = isApplicationInScope(application, effectiveMode, persona)
      const matchesStatus = statusFilter === 'all' || application.status === statusFilter
      const matchesOwner = ownerFilter === 'all' || application.submittedByEmail === ownerFilter
      const matchesSupervisor =
        supervisorFilter === 'all' || application.supervisorEmail === supervisorFilter
      const matchesArchive = matchesApplicationArchiveStatus(
        application,
        archiveStatusFilter,
        sourceArchiveOverrides,
      )
      const matchesKeyword =
        !normalizedKeyword ||
        [
          application.companyName,
          application.formTitle,
          application.contactName,
          application.contactEmail,
          application.submittedByName,
          application.supervisorName,
          application.needSummary,
          application.answers.map((answer) => answer.answer).join(' '),
        ]
          .join(' ')
          .toLocaleLowerCase()
          .includes(normalizedKeyword)
      return (
        scoped &&
        matchesStatus &&
        matchesOwner &&
        matchesSupervisor &&
        matchesArchive &&
        matchesKeyword
      )
    })
  }, [
    applications,
    archiveStatusFilter,
    effectiveMode,
    keyword,
    ownerFilter,
    persona,
    sourceArchiveOverrides,
    statusFilter,
    supervisorFilter,
  ])

  const selectedCustomer = useMemo(
    () =>
      selected
        ? applicationToCustomer(selected, {
            reviewerEmail: selected.reviewerEmail || selected.supervisorEmail,
            reviewerName: selected.reviewerName || selected.supervisorName,
          })
        : null,
    [selected],
  )
  const selectedComparisonCustomer = useMemo(
    () => (selected ? getApplicationCustomerMatch(selected, customers)?.customer : null),
    [customers, selected],
  )
  const selectedLinkedCustomer = useMemo(
    () => (selected ? getCustomerForApplication(selected.id, customers) : null),
    [customers, selected],
  )

  const selectedDuplicateSignals = useMemo<DuplicateCustomerSignal[]>(
    () =>
      featureVisibility['customers.duplicates'] && selected
        ? createDuplicateSignals(
            selected,
            customers.filter((customer) => customer.id !== selectedLinkedCustomer?.id),
          )
        : [],
    [customers, featureVisibility, selected, selectedLinkedCustomer?.id],
  )

  const selectedFieldValues = selected ? (fieldValuesByApplication[selected.id] ?? []) : []
  const selectedSourceLink = selected
    ? sourceLinks.find((link) => link.applicationId === selected.id && link.status === 'active')
    : undefined
  const pageCount = getB2bPageCount(visibleApplications, pageSize)
  const paginatedApplications = useMemo(
    () => paginateB2bRows(visibleApplications, currentPage, pageSize),
    [currentPage, pageSize, visibleApplications],
  )
  const paginationResetKey = [
    effectiveMode,
    keyword,
    ownerFilter,
    pageSize,
    persona?.email ?? '',
    archiveStatusFilter,
    statusFilter,
    supervisorFilter,
  ].join('|')

  useEffect(() => {
    void paginationResetKey
    setCurrentPage(1)
  }, [paginationResetKey])

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, pageCount))
  }, [pageCount])

  if (isLoading) {
    return <div className="grid min-h-dvh place-items-center text-sm text-slate-500">載入中...</div>
  }

  if (!canAccess) {
    return (
      <div className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-950">沒有新客戶申請權限</h1>
          <p className="mt-2 text-sm text-slate-500">
            你的 Email 尚未列入業務或主管名單，請先由管理員建立業務資料。
          </p>
        </div>
      </div>
    )
  }

  function openCreateDialog() {
    setDraft(
      createEmptyApplicationDraft(
        formOptions,
        persona?.territory,
        featureVisibility['customers.sourceRecords'] ? 'questionnaire_record' : 'manual',
      ),
    )
  }

  async function submitDraft() {
    if (!draft) return
    const selectedForm = formOptions.find((form) => form.id === draft.formId) ?? formOptions[0]
    setIsSubmittingDraft(true)
    try {
      if (draft.sourceType === 'questionnaire_record') {
        if (!draft.sourceRecord) {
          setDraft((current) =>
            current ? { ...current, sourceError: '請先選擇一筆可建立申請的問卷記錄。' } : current,
          )
          return
        }
        await createApplicationFromSourceRecord(
          draft.sourceRecord.id,
          user,
          createSourceRecordOverrides(draft),
        )
      } else {
        await createManualApplication(toApplicationInput(draft, selectedForm), user)
      }
      setDraft(null)
    } catch (caught) {
      setDraft((current) =>
        current
          ? {
              ...current,
              sourceError: formatApplicationCreateError(caught),
            }
          : current,
      )
    } finally {
      setIsSubmittingDraft(false)
    }
  }

  async function handleSourceRecordSelect(record: QuestionnaireSourceRecordContract) {
    setIsPreviewingSource(true)
    try {
      const preview = await previewCustomerApplicationFromSourceRecord(record.id)
      setDraft(
        createEmptyApplicationDraft(formOptions, persona?.territory, 'questionnaire_record', {
          sourceRecord: preview.record,
          fieldValues: preview.fieldValues,
        }),
      )
    } catch (caught) {
      setDraft((current) =>
        current
          ? {
              ...current,
              sourceType: 'questionnaire_record',
              sourceRecord: record,
              sourceError: formatApplicationCreateError(caught),
            }
          : createEmptyApplicationDraft(formOptions, persona?.territory, 'questionnaire_record', {
              sourceRecord: record,
              sourceError: formatApplicationCreateError(caught),
            }),
      )
    } finally {
      setIsPreviewingSource(false)
    }
  }

  function handleDraftSourceTypeChange(sourceType: CustomerApplicationSourceType) {
    setDraft(createEmptyApplicationDraft(formOptions, persona?.territory, sourceType))
  }

  function handleDraftFieldChange(fieldKey: ApplicationDraftFieldKey, value: string | number) {
    setDraft((current) =>
      current ? updateApplicationDraftField(current, fieldKey, value) : current,
    )
  }

  async function updateApplicationSourceArchiveStatus(
    application: CustomerApplication,
    nextArchiveStatus: QuestionnaireSourceRecordArchiveStatus,
  ) {
    const sourceRecordId = getApplicationSourceRecordId(application)
    if (!sourceRecordId) return
    setArchiveError('')
    setArchivingSourceRecordId(sourceRecordId)
    try {
      const record = await archiveQuestionnaireSourceRecord(sourceRecordId, nextArchiveStatus)
      setSourceArchiveOverrides((current) => ({
        ...current,
        [sourceRecordId]: toSourceArchiveOverride(record),
      }))
      await refreshWorkflowData({ resetArchiveOverrides: true })
    } catch (caught) {
      setArchiveError(caught instanceof Error ? caught.message : '無法更新填寫記錄封存狀態。')
    } finally {
      setArchivingSourceRecordId(null)
    }
  }

  async function refreshWorkflowData(options: { resetArchiveOverrides?: boolean } = {}) {
    await queryClient.invalidateQueries({ queryKey: ['business-workflow-data-client'] })
    const result = await workflowQuery.refetch()
    if (result.data) {
      hydrateWorkflow(result.data)
      if (options.resetArchiveOverrides) setSourceArchiveOverrides({})
    }
    setCurrentPage(1)
  }

  async function refreshApplicationsList() {
    setIsRefreshingApplications(true)
    try {
      await refreshWorkflowData({ resetArchiveOverrides: true })
    } finally {
      setIsRefreshingApplications(false)
    }
  }

  const canCreateApplication = effectiveMode === 'sales' || role === 'admin'
  const showCreateApplicationButton = featureVisibility['customers.createApplication']

  return (
    <div className="flex w-[100dvw] min-w-0 max-w-[100dvw] flex-col gap-5 overflow-hidden p-4 lg:w-[var(--miao-dashboard-main-width)] lg:max-w-[var(--miao-dashboard-main-width)] lg:p-6">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold">新客戶申請</h1>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void refreshApplicationsList()}
            disabled={isRefreshingApplications}
          >
            <RefreshCw className={cn('size-4', isRefreshingApplications && 'animate-spin')} />
            重新整理
          </Button>
          {showCreateApplicationButton ? (
            <Button onClick={openCreateDialog} disabled={!canCreateApplication}>
              <Plus className="size-4" />
              建立新客戶申請
            </Button>
          ) : null}
        </div>
      </div>

      <section className="grid min-w-0 gap-3 rounded-lg border border-slate-200 bg-white p-3 xl:grid-cols-[minmax(220px,1fr)_repeat(4,minmax(0,180px))]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={keyword}
            onChange={(event) => setKeyword(event.currentTarget.value)}
            placeholder="搜尋客戶、問卷、業務、主管、答案、需求"
            className="pl-9"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as StatusFilter)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部狀態</SelectItem>
            {Object.entries(applicationStatusMeta).map(([value, meta]) => (
              <SelectItem key={value} value={value}>
                {meta.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FilterSelect
          value={ownerFilter}
          onChange={setOwnerFilter}
          placeholder="提交業務"
          options={[
            { value: 'all', label: '全部提交業務' },
            ...ownerOptions.map(([value, label]) => ({ value, label })),
          ]}
        />
        <FilterSelect
          value={supervisorFilter}
          onChange={setSupervisorFilter}
          placeholder="所屬主管"
          options={[
            { value: 'all', label: '全部所屬主管' },
            ...supervisorOptions.map(([value, label]) => ({ value, label })),
          ]}
        />
        <FilterSelect
          value={archiveStatusFilter}
          onChange={(value) => setArchiveStatusFilter(value as SourceArchiveStatusFilter)}
          placeholder="封存狀態"
          options={archiveStatusOptions}
        />
      </section>

      {archiveError ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {archiveError}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        {visibleApplications.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>客戶</TableHead>
                  <TableHead>是否已有客戶</TableHead>
                  <TableHead>新客戶建檔選擇</TableHead>
                  <TableHead>提交業務</TableHead>
                  <TableHead>所屬主管</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedApplications.map((application) => {
                  const statusMeta = applicationStatusMeta[application.status]
                  const match = getApplicationCustomerMatch(application, customers)
                  const customerStatus = getApplicationCustomerStatusLabel(application, customers)
                  const linkedCustomer = getCustomerForApplication(application.id, customers)
                  const formTypeSummary = summarizeApplicationFormType(application)
                  const sourceRecordId = getApplicationSourceRecordId(application)
                  const sourceArchiveStatus = getApplicationSourceArchiveStatus(
                    application,
                    sourceArchiveOverrides,
                  )
                  const isArchived = sourceArchiveStatus === 'archived'
                  const duplicateSignals = featureVisibility['customers.duplicates']
                    ? createDuplicateSignals(
                        application,
                        customers.filter((customer) => customer.id !== linkedCustomer?.id),
                      )
                    : []
                  return (
                    <TableRow key={application.id} className="align-top">
                      <TableCell className="min-w-[220px]">
                        <div className="font-medium text-slate-950">{application.companyName}</div>
                        <div className="mt-1 text-xs text-slate-500">{application.contactName}</div>
                      </TableCell>
                      <TableCell className="min-w-[180px]">
                        <Badge
                          variant={
                            customerStatus.label === '尚未更新客戶資料'
                              ? 'secondary'
                              : customerStatus.label === '已更新客戶資料'
                                ? 'success'
                                : 'warning'
                          }
                        >
                          {customerStatus.label}
                        </Badge>
                        <div className="mt-1 text-xs text-slate-500">
                          {customerStatus.description}
                        </div>
                      </TableCell>
                      <TableCell className="min-w-[260px]">
                        <div className="text-sm font-medium text-slate-900">{formTypeSummary}</div>
                        {linkedCustomer ? (
                          <div className="mt-1 text-xs text-slate-500">
                            已更新客戶資料：{linkedCustomer.companyName}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>{application.submittedByName}</TableCell>
                      <TableCell>{application.supervisorName}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1.5">
                          <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>
                          {duplicateSignals.length > 0 ? (
                            <Badge variant="warning">可能重複</Badge>
                          ) : null}
                          {isArchived ? <Badge variant="secondary">已封存</Badge> : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                          onClick={() => setSelected(application)}
                        >
                          查看
                        </Button>
                        {sourceRecordId ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={archivingSourceRecordId === sourceRecordId}
                            onClick={() =>
                              void updateApplicationSourceArchiveStatus(
                                application,
                                isArchived ? 'active' : 'archived',
                              )
                            }
                          >
                            {isArchived ? (
                              <ArchiveRestore className="size-4" />
                            ) : (
                              <Archive className="size-4" />
                            )}
                            {archivingSourceRecordId === sourceRecordId
                              ? '處理中'
                              : isArchived
                                ? '取消封存'
                                : '封存'}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            <B2bPagination
              currentPage={currentPage}
              pageCount={pageCount}
              pageSize={pageSize}
              totalCount={visibleApplications.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={(nextPageSize) => {
                setPageSize(nextPageSize)
                setCurrentPage(1)
              }}
            />
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center">
            <ClipboardList className="mx-auto size-10 text-slate-300" />
            <h2 className="mt-3 font-semibold text-slate-900">目前沒有符合條件的新客戶申請</h2>
            <p className="mt-1 text-sm text-slate-500">可切換角色視角或清除搜尋條件後再確認。</p>
          </div>
        )}
      </div>

      <ApplicationDialog
        draft={draft}
        duplicateSignals={
          featureVisibility['customers.duplicates'] && draft
            ? createDuplicateSignals(draft, customers)
            : []
        }
        formOptions={formOptions}
        isPreviewingSource={isPreviewingSource}
        isSubmitting={isSubmittingDraft}
        onChange={(patch) => setDraft((current) => (current ? { ...current, ...patch } : current))}
        onFieldChange={handleDraftFieldChange}
        onOpenChange={(open) => {
          if (!open) setDraft(null)
        }}
        onSourceRecordSelect={(record) => void handleSourceRecordSelect(record)}
        onSourceTypeChange={handleDraftSourceTypeChange}
        showSourceFieldValues={featureVisibility['customers.sourceFields']}
        showSourceRecordWorkflow={featureVisibility['customers.sourceRecords']}
        onSubmit={submitDraft}
      />

      <CustomerDetailDialog
        open={Boolean(selected)}
        customer={selectedCustomer}
        sourceApplication={selected}
        comparisonCustomer={selectedComparisonCustomer}
        sourceLink={selectedSourceLink}
        fieldValues={selectedFieldValues}
        duplicateSignals={selectedDuplicateSignals}
        canEdit={false}
        onOpenChange={(open) => {
          if (!open) setSelected(null)
        }}
      />
    </div>
  )
}

function ApplicationCard({
  application,
  duplicateSignals,
  filingStatus,
  onOpen,
}: {
  application: CustomerApplication
  duplicateSignals: DuplicateCustomerSignal[]
  filingStatus?: keyof typeof customerFilingStatusMeta
  onOpen: () => void
}) {
  const status = applicationStatusMeta[application.status]
  const filing = filingStatus ? customerFilingStatusMeta[filingStatus] : null
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <button type="button" onClick={onOpen} className="min-w-0 text-left">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold text-slate-950">{application.companyName}</h2>
              <Badge variant={status.variant}>{status.label}</Badge>
              <Badge variant={filing?.variant ?? 'outline'}>
                {filing?.label ??
                  (application.status === 'approved_pending_erp_filing' ||
                  application.status === 'customer_created'
                    ? 'ERP未建檔'
                    : '尚未更新客戶資料')}
              </Badge>
              {duplicateSignals.length > 0 ? <Badge variant="warning">可能重複</Badge> : null}
            </div>
            <p className="mt-2 line-clamp-2 text-sm text-slate-500">{application.needSummary}</p>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
              <span>問卷：{application.formTitle}</span>
              <span>業務：{application.submittedByName}</span>
              <span>主管：{application.supervisorName}</span>
              <span>團隊：{application.teamName}</span>
              <span>送出：{application.submittedAt}</span>
            </div>
          </button>
          <div className="grid gap-2 text-sm md:grid-cols-3 xl:min-w-[520px]">
            <MiniInfo label="聯絡人" value={application.contactName || '-'} />
            <MiniInfo label="Email" value={application.contactEmail || '-'} />
            <MiniInfo
              label="建檔進度"
              value={
                filing?.label ??
                (application.status === 'approved_pending_erp_filing' ||
                application.status === 'customer_created'
                  ? 'ERP未建檔'
                  : '尚未更新客戶資料')
              }
            />
          </div>
          <Button onClick={onOpen}>查看詳情</Button>
        </div>
      </CardContent>
    </Card>
  )
}

function ApplicationDialog({
  draft,
  duplicateSignals,
  formOptions,
  isPreviewingSource,
  isSubmitting,
  onChange,
  onFieldChange,
  onOpenChange,
  onSourceRecordSelect,
  onSourceTypeChange,
  showSourceFieldValues,
  showSourceRecordWorkflow,
  onSubmit,
}: {
  draft: ApplicationDraft | null
  duplicateSignals: DuplicateCustomerSignal[]
  formOptions: { id: string; title: string; description: string }[]
  isPreviewingSource: boolean
  isSubmitting: boolean
  onChange: (patch: Partial<ApplicationDraft>) => void
  onFieldChange: (fieldKey: ApplicationDraftFieldKey, value: string | number) => void
  onOpenChange: (open: boolean) => void
  onSourceRecordSelect: (record: QuestionnaireSourceRecordContract) => void
  onSourceTypeChange: (sourceType: CustomerApplicationSourceType) => void
  showSourceFieldValues: boolean
  showSourceRecordWorkflow: boolean
  onSubmit: () => void
}) {
  type ApplicationDialogTab = 'management' | 'company' | 'sales' | 'source'
  const [activeApplicationTab, setActiveApplicationTab] = useState<ApplicationDialogTab>('company')
  const fieldValueMap = useMemo(
    () => new Map(draft?.fieldValues.map((fieldValue) => [fieldValue.fieldKey, fieldValue]) ?? []),
    [draft?.fieldValues],
  )
  const canSubmit =
    Boolean(draft) &&
    !isSubmitting &&
    (draft?.sourceType === 'manual' || Boolean(draft?.sourceRecord))
  const formSelectOptions = useMemo(
    () => mergeFormOptions(formOptions, draft?.sourceRecord),
    [draft?.sourceRecord, formOptions],
  )
  const rightPaneScrollKey = `${draft?.sourceType ?? 'closed'}:${draft?.sourceRecord?.id ?? 'none'}:${activeApplicationTab}`
  const draftResetKey = `${draft?.sourceType ?? 'closed'}:${draft?.sourceRecord?.id ?? 'none'}`

  useEffect(() => {
    if (draftResetKey !== 'closed:none') setActiveApplicationTab('company')
  }, [draftResetKey])

  useEffect(() => {
    if (draft?.sourceType === 'questionnaire_record' && !showSourceRecordWorkflow) {
      onSourceTypeChange('manual')
    }
  }, [draft?.sourceType, onSourceTypeChange, showSourceRecordWorkflow])

  useEffect(() => {
    if (activeApplicationTab === 'source' && !showSourceFieldValues) {
      setActiveApplicationTab('company')
    }
  }, [activeApplicationTab, showSourceFieldValues])

  return (
    <Dialog open={Boolean(draft)} onOpenChange={onOpenChange}>
      <DialogContent className="bottom-[10px] left-[10px] right-[10px] top-[10px] flex max-h-none w-auto max-w-none translate-x-0 translate-y-0 flex-col overflow-hidden p-0">
        <DialogTitle className="sr-only">填寫新客戶申請</DialogTitle>
        <DialogDescription className="sr-only">
          可從一筆問卷記錄建立新客戶申請，也可手動建立不指定問卷記錄的申請。
        </DialogDescription>
        {draft ? (
          <>
            <div className="grid min-h-0 flex-1 lg:grid-cols-[430px_minmax(0,1fr)]">
              <aside className="flex min-h-0 flex-col border-b border-slate-200 bg-slate-50 lg:border-b-0 lg:border-r">
                <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-950">填寫新客戶申請</h2>
                      <p className="mt-1 text-sm text-slate-500">
                        {showSourceRecordWorkflow
                          ? '選擇來源問卷記錄，或改用手動建立不指定記錄的申請。'
                          : '來源問卷記錄已由系統功能設定隱藏，請以手動方式建立申請。'}
                      </p>
                    </div>
                  </div>
                  {showSourceRecordWorkflow ? (
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        variant={
                          draft.sourceType === 'questionnaire_record' ? 'default' : 'outline'
                        }
                        aria-pressed={draft.sourceType === 'questionnaire_record'}
                        onClick={() => onSourceTypeChange('questionnaire_record')}
                      >
                        <Link2 className="size-4" />
                        問卷記錄
                      </Button>
                      <Button
                        type="button"
                        variant={draft.sourceType === 'manual' ? 'default' : 'outline'}
                        aria-pressed={draft.sourceType === 'manual'}
                        onClick={() => onSourceTypeChange('manual')}
                      >
                        <FileText className="size-4" />
                        手動建立
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
                      <div className="flex items-center gap-2 font-semibold text-slate-950">
                        <FileText className="size-4 text-indigo-600" />
                        手動建立模式
                      </div>
                      <p className="mt-2 text-sm text-slate-500">
                        這個 workflow 目前只顯示手動建立，不會讀取問卷來源記錄。
                      </p>
                    </div>
                  )}
                  {isPreviewingSource ? (
                    <div className="mt-3 inline-flex items-center gap-2 text-xs text-slate-500">
                      <Loader2 className="size-3 animate-spin" />
                      正在帶入欄位...
                    </div>
                  ) : null}
                  {draft.sourceError ? (
                    <div className="mt-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                      <AlertCircle className="mt-0.5 size-4 shrink-0" />
                      {draft.sourceError}
                    </div>
                  ) : null}
                </div>
                <div className="min-h-0 flex-1">
                  {draft.sourceType === 'questionnaire_record' && showSourceRecordWorkflow ? (
                    <CustomerApplicationSourceRecordPicker
                      selectedId={draft.sourceRecord?.id}
                      onSelect={onSourceRecordSelect}
                      className="h-full rounded-none border-0 bg-transparent"
                    />
                  ) : (
                    <div className="p-5">
                      <div className="rounded-lg border border-slate-200 bg-white p-4">
                        <div className="flex items-center gap-2 font-semibold text-slate-950">
                          <FileText className="size-4 text-indigo-600" />
                          手動建立申請
                        </div>
                        <p className="mt-2 text-sm text-slate-500">
                          此申請不指定問卷記錄，所有欄位都會標示為手動輸入。
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </aside>
              <section className="flex min-h-0 flex-col bg-white">
                <Tabs
                  value={activeApplicationTab}
                  onValueChange={(value) => setActiveApplicationTab(value as ApplicationDialogTab)}
                  className="flex min-h-0 flex-1 flex-col"
                >
                  <div className="shrink-0 border-b border-slate-200 px-6 py-4">
                    <TabsList className="h-auto flex-wrap justify-start">
                      <TabsTrigger value="management">客戶管理</TabsTrigger>
                      <TabsTrigger value="company">公司資料</TabsTrigger>
                      <TabsTrigger value="sales">業務 / 授信</TabsTrigger>
                      {showSourceFieldValues ? (
                        <TabsTrigger value="source">來源欄位</TabsTrigger>
                      ) : null}
                    </TabsList>
                  </div>
                  <ScrollArea
                    key={rightPaneScrollKey}
                    className="min-h-0 flex-1"
                    viewportClassName="px-6 py-5"
                  >
                    <TabsContent value="management" className="mt-0">
                      {duplicateSignals.length > 0 ? (
                        <div className="mb-4">
                          <DuplicateResolutionPanel duplicateSignals={duplicateSignals} compact />
                        </div>
                      ) : null}
                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        <SelectField
                          label="對應問卷"
                          value={draft.formId}
                          disabled={draft.sourceType === 'questionnaire_record'}
                          options={formSelectOptions}
                          onChange={(formId) => onChange({ formId })}
                        />
                        <div className="space-y-2">
                          <Label>來源</Label>
                          <Input
                            value={draft.source}
                            onChange={(event) => onChange({ source: event.currentTarget.value })}
                          />
                        </div>
                        {draft.sourceRecord ? (
                          <div className="rounded-md bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
                            <div className="text-xs font-medium text-indigo-600">來源問卷記錄</div>
                            <div className="mt-1 truncate">{draft.sourceRecord.responseId}</div>
                          </div>
                        ) : null}
                        <ApplicationDraftFieldGrid
                          fields={managementFieldKeys}
                          draft={draft}
                          fieldValueMap={fieldValueMap}
                          onFieldChange={onFieldChange}
                        />
                      </div>
                    </TabsContent>
                    <TabsContent value="company" className="mt-0">
                      <ApplicationDraftFieldGrid
                        fields={companyQuestionFieldKeys}
                        draft={draft}
                        fieldValueMap={fieldValueMap}
                        onFieldChange={onFieldChange}
                      />
                    </TabsContent>
                    <TabsContent value="sales" className="mt-0">
                      <ApplicationDraftFieldGrid
                        fields={salesCreditFieldKeys}
                        draft={draft}
                        fieldValueMap={fieldValueMap}
                        onFieldChange={onFieldChange}
                      />
                    </TabsContent>
                    {showSourceFieldValues ? (
                      <TabsContent value="source" className="mt-0">
                        <SourceFieldValuesPanel values={draft.fieldValues} />
                      </TabsContent>
                    ) : null}
                  </ScrollArea>
                </Tabs>
              </section>
            </div>
            <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button
                onClick={onSubmit}
                disabled={!canSubmit}
                title={
                  !canSubmit ? '請先選擇可建立申請的問卷記錄，或切換為手動建立。' : '送出新客戶申請'
                }
              >
                {isSubmitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
                {draft.sourceType === 'questionnaire_record' ? '建立並送審' : '送出主管審核'}
              </Button>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function ApplicationDraftFieldGrid({
  fields,
  draft,
  fieldValueMap,
  onFieldChange,
}: {
  fields: ApplicationDraftFieldKey[]
  draft: ApplicationDraft
  fieldValueMap: Map<CustomerFieldMappingTarget, CustomerApplicationFieldValueContract>
  onFieldChange: (fieldKey: ApplicationDraftFieldKey, value: string | number) => void
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {applicationFieldConfigs
        .filter((field) => fields.includes(field.key))
        .map((field) => (
          <ApplicationDraftField
            key={field.key}
            field={field}
            value={draft[field.key]}
            fieldValue={fieldValueMap.get(field.key)}
            onChange={(value) => onFieldChange(field.key, value)}
          />
        ))}
    </div>
  )
}

function SourceFieldValuesPanel({ values }: { values: CustomerApplicationFieldValueContract[] }) {
  return (
    <ErpFieldMappingTable
      rows={buildErpRowsFromFieldValues(values)}
      title="來源問卷 ERP 客戶欄位對照"
      description="確認每個問卷答案帶入申請表時，會對應到哪個 ERP 客戶欄位與 DB 欄位。"
      emptyMessage="尚未選擇來源問卷記錄，或這份問卷尚未設定欄位 binding。"
    />
  )
}

function mergeFormOptions(
  formOptions: { id: string; title: string; description: string }[],
  sourceRecord: QuestionnaireSourceRecordContract | undefined,
) {
  const options = formOptions.map((form) => ({ value: form.id, label: form.title }))
  if (sourceRecord && !options.some((option) => option.value === sourceRecord.formId)) {
    return [{ value: sourceRecord.formId, label: sourceRecord.formTitle }, ...options]
  }
  return options
}

function createEmptyApplicationDraft(
  formOptions: { id: string; title: string; description: string }[],
  defaultSalesRegion: string | undefined,
  sourceType: CustomerApplicationSourceType,
  options: {
    sourceRecord?: QuestionnaireSourceRecordContract
    fieldValues?: CustomerApplicationFieldValueContract[]
    sourceError?: string
  } = {},
): ApplicationDraft {
  const firstForm = formOptions[0]
  const fieldPatch = valuesToDraftPatch(options.fieldValues ?? [])
  return {
    sourceType,
    sourceRecord: options.sourceRecord,
    fieldValues: options.fieldValues ?? [],
    sourceError: options.sourceError,
    formId: options.sourceRecord?.formId ?? firstForm?.id ?? '',
    companyName: fieldPatch.companyName ?? '',
    taxId: fieldPatch.taxId ?? '',
    industry: fieldPatch.industry ?? '',
    companySize: fieldPatch.companySize ?? '',
    website: fieldPatch.website ?? '',
    companyAddress: fieldPatch.companyAddress ?? '',
    companyPhone: fieldPatch.companyPhone ?? '',
    contactName: fieldPatch.contactName ?? '',
    contactTitle: fieldPatch.contactTitle ?? '',
    contactEmail: fieldPatch.contactEmail ?? '',
    contactPhone: fieldPatch.contactPhone ?? '',
    decisionRole: fieldPatch.decisionRole ?? '',
    paymentTerms: fieldPatch.paymentTerms ?? '',
    creditLimit: toDraftNumber(fieldPatch.creditLimit),
    salesRegion: fieldPatch.salesRegion ?? defaultSalesRegion ?? '',
    expectedValue: toDraftNumber(fieldPatch.expectedValue),
    source: sourceType === 'questionnaire_record' ? '問卷記錄' : '手動建立',
    needSummary: fieldPatch.needSummary ?? '',
    internalNotes:
      fieldPatch.internalNotes ??
      (sourceType === 'questionnaire_record' ? '由問卷記錄帶入，請檢查欄位來源與覆寫狀態。' : ''),
  }
}

function valuesToDraftPatch(values: CustomerApplicationFieldValueContract[]) {
  const fieldMap = Object.fromEntries(
    values.map((value) => [value.fieldKey, value.value]),
  ) as Partial<Record<CustomerFieldMappingTarget, string>>
  return {
    companyName: firstDraftValue(fieldMap, 'customerShortName', 'companyName', 'companyFullName'),
    taxId: firstDraftValue(fieldMap, 'taxId'),
    industry: firstDraftValue(fieldMap, 'industry', 'customerChannel', 'customerCategory'),
    companySize: firstDraftValue(fieldMap, 'companySize', 'employeeCountRange'),
    website: firstDraftValue(fieldMap, 'website'),
    companyAddress: firstDraftValue(
      fieldMap,
      'companyAddress',
      'deliveryAddress',
      'registeredAddress',
      'invoiceAddress',
    ),
    companyPhone: firstDraftValue(fieldMap, 'companyPhone', 'phone1'),
    contactName: firstDraftValue(fieldMap, 'contactName'),
    contactTitle: firstDraftValue(fieldMap, 'contactTitle', 'ownerName'),
    contactEmail: firstDraftValue(fieldMap, 'contactEmail', 'email', 'billingEmail'),
    contactPhone: firstDraftValue(fieldMap, 'contactPhone', 'phone1', 'phone2'),
    decisionRole: firstDraftValue(fieldMap, 'decisionRole'),
    paymentTerms: firstDraftValue(fieldMap, 'paymentTerms'),
    creditLimit: firstDraftValue(fieldMap, 'creditLimit'),
    salesRegion: firstDraftValue(fieldMap, 'salesRegion', 'region'),
    expectedValue: firstDraftValue(fieldMap, 'expectedValue'),
    needSummary: firstDraftValue(fieldMap, 'needSummary', 'remark'),
    internalNotes: firstDraftValue(fieldMap, 'internalNotes', 'remark'),
  } satisfies Partial<Record<ApplicationDraftFieldKey, string>>
}

function firstDraftValue(
  fieldMap: Partial<Record<CustomerFieldMappingTarget, string>>,
  ...fields: CustomerFieldMappingTarget[]
) {
  return fields.map((field) => fieldMap[field]).find((value) => value?.trim()) ?? ''
}

function toDraftNumber(value: string | undefined) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function updateApplicationDraftField(
  draft: ApplicationDraft,
  fieldKey: ApplicationDraftFieldKey,
  value: string | number,
): ApplicationDraft {
  const stringValue = String(value ?? '')
  return {
    ...draft,
    [fieldKey]: value,
    fieldValues: overrideApplicationFieldValue(draft.fieldValues, fieldKey, stringValue),
    sourceError: undefined,
  }
}

function createSourceRecordOverrides(draft: ApplicationDraft) {
  const overrides: Partial<Record<CustomerFieldMappingTarget, string | number | undefined>> = {}
  for (const field of applicationFieldConfigs) {
    const currentValue = draft[field.key]
    const fieldValue = draft.fieldValues.find((item) => item.fieldKey === field.key)
    const normalized = String(currentValue ?? '')
    if (!fieldValue) {
      if (normalized.trim()) overrides[field.key] = currentValue
      continue
    }
    const originalValue = fieldValue.originalValue ?? fieldValue.value
    if (fieldValue.sourceState === 'overridden' || normalized !== String(originalValue ?? '')) {
      overrides[field.key] = currentValue
    }
  }
  return overrides
}

function toApplicationInput(
  draft: ApplicationDraft,
  selectedForm: { id: string; title: string; description: string } | undefined,
) {
  return {
    formId: draft.formId,
    formTitle: selectedForm?.title ?? '手動新客戶申請',
    formDescription: selectedForm?.description ?? '不指定問卷記錄，由業務自行建立後送主管審核。',
    companyName: draft.companyName,
    taxId: draft.taxId,
    industry: draft.industry,
    companySize: draft.companySize,
    website: draft.website,
    companyAddress: draft.companyAddress,
    companyPhone: draft.companyPhone,
    contactName: draft.contactName,
    contactTitle: draft.contactTitle,
    contactEmail: draft.contactEmail,
    contactPhone: draft.contactPhone,
    decisionRole: draft.decisionRole,
    paymentTerms: draft.paymentTerms,
    creditLimit: draft.creditLimit,
    salesRegion: draft.salesRegion,
    expectedValue: draft.expectedValue,
    source: draft.source,
    needSummary: draft.needSummary,
    internalNotes: draft.internalNotes,
  }
}

function formatApplicationCreateError(caught: unknown) {
  if (caught instanceof CustomerApplicationBindingError) return caught.message
  if (caught instanceof Error) return caught.message
  return '無法建立新客戶申請，請稍後再試。'
}

function getEffectiveMode(role: BusinessRole): ViewMode {
  if (role === 'admin') return 'admin'
  if (role === 'manager') return 'manager'
  return 'sales'
}

function getPersonaForMode(
  viewMode: ViewMode,
  email: string | null | undefined,
  salesUsers: ReturnType<typeof useBusinessWorkflowStore.getState>['salesUsers'],
) {
  const current = getSalesUserByEmail(email, salesUsers)
  if (viewMode === 'manager') {
    return current?.role === 'manager'
      ? current
      : salesUsers.find((salesUser) => salesUser.role === 'manager')
  }
  if (viewMode === 'sales') {
    return current && isSalesContributorRole(current.role)
      ? current
      : salesUsers.find((salesUser) => isSalesContributorRole(salesUser.role))
  }
  return current
}

function isApplicationInScope(
  application: CustomerApplication,
  viewMode: ViewMode,
  persona: ReturnType<typeof getPersonaForMode>,
) {
  if (viewMode === 'admin') return true
  void application
  return Boolean(persona)
}

function B2bPagination({
  currentPage,
  pageCount,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}: {
  currentPage: number
  pageCount: number
  pageSize: number
  totalCount: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm text-slate-500">
      <span>
        共 {totalCount} 筆，第 {currentPage} / {pageCount} 頁
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <Label htmlFor="customer-applications-page-size" className="text-xs text-slate-500">
          每頁筆數
        </Label>
        <Select value={String(pageSize)} onValueChange={(value) => onPageSizeChange(Number(value))}>
          <SelectTrigger
            id="customer-applications-page-size"
            aria-label="每頁筆數"
            className="h-9 w-24"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {B2B_PAGE_SIZE_OPTIONS.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option} 筆
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        >
          上一頁
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={currentPage >= pageCount}
          onClick={() => onPageChange(Math.min(pageCount, currentPage + 1))}
        >
          下一頁
        </Button>
      </div>
    </div>
  )
}

function MiniInfo({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 truncate font-medium">{value}</div>
    </div>
  )
}

function ApplicationDraftField({
  field,
  value,
  fieldValue,
  onChange,
}: {
  field: (typeof applicationFieldConfigs)[number]
  value: string | number
  fieldValue?: CustomerApplicationFieldValueContract
  onChange: (value: string | number) => void
}) {
  const id = `application-${field.key}`
  return (
    <div className={cn('space-y-2', field.className)}>
      <div className="flex min-h-5 flex-wrap items-center justify-between gap-2">
        <Label htmlFor={id}>{field.label}</Label>
        <ApplicationFieldSourceBadge fieldValue={fieldValue} compact />
      </div>
      {field.kind === 'textarea' ? (
        <Textarea
          id={id}
          className="min-h-24"
          value={String(value ?? '')}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      ) : (
        <Input
          id={id}
          type={field.kind === 'number' ? 'number' : 'text'}
          value={value}
          onChange={(event) =>
            onChange(
              field.kind === 'number'
                ? Number(event.currentTarget.value) || 0
                : event.currentTarget.value,
            )
          }
        />
      )}
      {fieldValue?.originalValue && fieldValue.sourceState === 'overridden' ? (
        <p className="text-xs text-amber-700">原始值：{fieldValue.originalValue}</p>
      ) : null}
      {fieldValue?.sourceState === 'missing_source' ? (
        <p className="text-xs text-slate-500">已設定欄位綁定，但來源答案未填或無法轉換。</p>
      ) : null}
    </div>
  )
}

function SelectField({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
  disabled?: boolean
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger disabled={disabled}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function FilterSelect({
  value,
  options,
  onChange,
  placeholder,
}: {
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
  placeholder?: string
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={placeholder} className="min-w-0">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
